/**
 * The catalogue of allergens and additives of a business (docs/08-entscheidungen.md, S5): a short
 * code, its name and its kind. Dishes name codes; a menu shows them by the name and lists the ones
 * it uses at the bottom. Every business has the 14 allergens of the food information regulation
 * (LMIV) as a–n, fixed (Martin, 01.11.2026: „die sind fix. Punkt.“), and starts with 14 additives as
 * 1–14; it may add allergens of its own (a1 Weizen) and change, add or remove additives. The default
 * list comes from household-kit (food, geprüfte Kopie in packages/shared/food), as in KellerLog.
 */
import { ADDITIVES, ALLERGENS } from "@household-kit/food";

export type AllergenKind = "allergen" | "additive";

export interface Allergen {
  code: string;
  name: string;
  kind: AllergenKind;
}

export const KIND_LABELS: Record<AllergenKind, string> = { allergen: "Allergen", additive: "Zusatzstoff" };

/** The 14 main allergens of the LMIV (annex II), with the letters German menus use, from household-kit (food). */
export const LMIV_ALLERGENS: readonly Allergen[] = ALLERGENS.map((a) => ({ code: a.code, name: a.name, kind: "allergen" }));

/**
 * Additives a menu has to name, as numbers, from household-kit (food): 1–13 as before, 14 „erhöhter
 * Koffeingehalt“ (from KellerLog) for new businesses since 01.11.2026 (Martin).
 */
export const DEFAULT_ADDITIVES: readonly Allergen[] = ADDITIVES.map((a) => ({ code: a.code, name: a.name, kind: "additive" }));

export const DEFAULT_ALLERGENS: readonly Allergen[] = [...LMIV_ALLERGENS, ...DEFAULT_ADDITIVES];

const LMIV_CODES = new Set(LMIV_ALLERGENS.map((a) => a.code));
/** One of the 14 of the LMIV: neither renamed nor removed nor an additive. */
export const isLmiv = (code: string) => LMIV_CODES.has(code);

/** A catalogue with the 14 of the LMIV first and as the regulation names them; the business's own after. */
export const withLmiv = (list: readonly Allergen[]): Allergen[] => [...LMIV_ALLERGENS.map((a) => ({ ...a })), ...list.filter((a) => !isLmiv(a.code))];

/** A code as stored: lower case, without brackets and spaces („A)“ → „a“). */
export const normCode = (raw: string) => raw.trim().toLowerCase().replace(/^[([]+|[)\].]+$/g, "");

const CODE = /^[a-z0-9]{1,4}$/;
export const isCode = (code: string) => CODE.test(code);

/** Words that say „contains none“. */
const NONE = new Set(["keine", "kein", "ohne", "-", "–", "—", "0x"]);

/**
 * The codes in a cell or a field: „a, c, j“, „a,c,j“, „a c j“, „13“. Empty is null (not kept up);
 * „keine“ or „-“ is an empty list (contains none). Codes not in the catalogue come back as `unknown`.
 */
export function parseCodes(raw: unknown, known: ReadonlySet<string>): { codes: string[] | null; unknown: string[] } {
  if (raw === null || raw === undefined) return { codes: null, unknown: [] };
  if (Array.isArray(raw)) return parseCodes(raw.join(","), known);
  const text = String(raw).trim();
  if (text === "") return { codes: null, unknown: [] };
  if (NONE.has(text.toLowerCase())) return { codes: [], unknown: [] };
  const parts = text.split(/[,;/\s]+/).map(normCode).filter(Boolean);
  const codes: string[] = [];
  const unknown: string[] = [];
  for (const p of parts) {
    if (!known.has(p)) {
      if (!unknown.includes(p)) unknown.push(p);
    } else if (!codes.includes(p)) codes.push(p);
  }
  return { codes, unknown };
}

/** Codes in the order of the catalogue (allergens first, then additives, as listed). */
export function sortCodes(codes: readonly string[], catalog: readonly Allergen[]): string[] {
  const order = new Map(catalog.map((a, i) => [a.code, i]));
  return [...codes].sort((a, b) => (order.get(a) ?? 999) - (order.get(b) ?? 999) || a.localeCompare(b, "de", { numeric: true }));
}

/** „a, c, j“ for a cell or a field; null is empty, none is „keine“. */
export const codesText = (codes: readonly string[] | null): string => (codes === null ? "" : codes.length === 0 ? "keine" : codes.join(", "));

/**
 * A catalogue as entered: codes unique and valid, names given, at most 60 entries; the 14 of the
 * LMIV as they are (one left out comes back). Returns the cleaned list or the first problem.
 */
export function cleanCatalog(input: unknown): { value: Allergen[] } | { error: string } {
  if (!Array.isArray(input)) return { error: "Ungültige Liste" };
  if (input.length > 60) return { error: "Höchstens 60 Allergene und Zusatzstoffe" };
  const out: Allergen[] = [];
  for (const raw of input) {
    const o = (raw ?? {}) as Record<string, unknown>;
    const code = normCode(String(o.code ?? ""));
    const name = String(o.name ?? "").replace(/\s+/g, " ").trim();
    const kind: AllergenKind = o.kind === "additive" ? "additive" : "allergen";
    if (!isCode(code)) return { error: `Kürzel „${String(o.code ?? "")}“: 1–4 Buchstaben oder Ziffern` };
    if (name === "" || name.length > 80) return { error: `Kürzel „${code}“: Bitte eine Bezeichnung (höchstens 80 Zeichen)` };
    const lmiv = LMIV_ALLERGENS.find((a) => a.code === code);
    if (lmiv && (lmiv.name !== name || kind !== "allergen")) return { error: `Das Kürzel „${code}“ gehört zu den 14 Allergenen der LMIV („${lmiv.name}“). Bitte ein anderes, etwa „${code}1“.` };
    if (out.some((a) => a.code === code)) return { error: `Das Kürzel „${code}“ steht zweimal in der Liste` };
    out.push({ code, name, kind });
  }
  return { value: withLmiv(out) };
}
